import { getColorById, keralitProfileSystems, isKeralitProfileSystem, normalizeKeralitProfileSystem } from '../data/mockData';
import {
  calcVoordeurWoodJointPremium,
  getConfiguratorGlazingAreaSqm,
} from './pdfPricing';
import { getVmkGlazingLabel, VMK_GLAZING_LABEL } from './vmkGlazing';

const FIXED_FRAME_OFFSET_MM = 140;
const MULTI_PART_GLASS_WIDTH_OFFSET_MM = 96;


export const VMK_PROFILE_NAME = 'Blokkader 15 graden';
export const VMK_DOOR_SUBTITLE = 'BUVA VERDIEPT 55 - scharnier: GEEN RAL KLEUR';
export { VMK_GLAZING_LABEL } from './vmkGlazing';

export const getLetterLabel = (index) => String.fromCharCode(65 + index);

export const detectPageType = (config) => {
  if (config.doorStyle !== undefined) return 'voordeur';
  if (config.topLight !== undefined || config.numberOfParts !== undefined) return 'achterdeur';
  if (config.designType !== undefined) return 'terrasdeur';
  if (config.doorType === 'sliding' || config.slidingPosition) return 'schuifpui';
  return 'kozijnen';
};

export const isKeralitQuote = (quoteOrConfig, quoteType) => {
  if (quoteType === 'keralit') return true;
  const config = quoteOrConfig?.config || quoteOrConfig;
  if (!config) return false;
  return isKeralitProfileSystem(config.profileSystem);
};

export const getKeralitProfileName = (profileSystemId) => {
  const normalized = normalizeKeralitProfileSystem(profileSystemId);
  const profile = keralitProfileSystems.find((item) => item.id === normalized);
  return profile?.name || keralitProfileSystems[0]?.name || 'Potdeksel 190';
};

const getPartWidths = (config, parts, totalWidth) => {
  if (Array.isArray(config.partWidths) && config.partWidths.length === parts) {
    const sum = config.partWidths.reduce((acc, value) => acc + Number(value), 0);
    if (sum > 0) return config.partWidths.map((value) => Number(value));
  }
  const base = Math.floor(totalWidth / parts);
  const widths = Array(parts).fill(base);
  widths[parts - 1] += totalWidth - base * parts;
  return widths;
};

export const formatMm = (value) => `${Math.round(value)}mm`;

/** Parse a dimension string like "1360mm" to millimeters. */
export const parseMmDimension = (value) => {
  const match = String(value || '').match(/^(\d+(?:[.,]\d+)?)\s*mm$/i);
  if (!match) return null;
  return Number(String(match[1]).replace(',', '.'));
};

/** Format a square-meter area for VMK quote tables, e.g. "1,85 m²". */
export const formatSqm = (areaSqm) => {
  if (!Number.isFinite(areaSqm) || areaSqm <= 0) return '';
  const formatted = areaSqm.toLocaleString('nl-NL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${formatted} m²`;
};

/** Convert width × height mm strings to a single m² display value. */
export const sqmDisplayFromMmDims = (dim1, dim2) => {
  const w = parseMmDimension(dim1);
  const h = parseMmDimension(dim2);
  if (w == null || h == null || w <= 0 || h <= 0) {
    return { dim1: dim1 || '', dim2: dim2 || '' };
  }
  return { dim1: formatSqm((w * h) / 1_000_000), dim2: '' };
};

/** Single drawing area in m² (quantity included). */
export const getQuoteAreaSqm = (quote) => {
  const quantity = quote?.config?.quantity || 1;
  let area = parseFloat(quote?.dimensions?.area);
  if (!Number.isFinite(area) || area <= 0) {
    const w = Number(quote?.config?.width ?? quote?.dimensions?.width);
    const h = Number(quote?.config?.height ?? quote?.dimensions?.height);
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      area = (w * h) / 1_000_000;
    } else {
      area = 0;
    }
  }
  return area * quantity;
};

/** Sum of all drawing areas in m². */
export const getTotalQuotesAreaSqm = (quotes = []) =>
  quotes.reduce((sum, quote) => sum + getQuoteAreaSqm(quote), 0);

const VMK_DUTCH_COLOR_NAMES = {
  'Light Ivory': 'Licht ivoor',
  'Wine Red': 'Wijnrood',
  Red: 'Rood',
  'Ultramarine Blue': 'Ultramarine blauw',
  'Monument Blue': 'Monument blauw',
  'Steel Blue': 'Staalblauw',
  'Cobalt Blue': 'Kobaltblauw',
  'Brilliant Blue': 'Brilliant blauw',
  'Fir Green': 'Dennegroen',
  'Moss Green': 'Mosgroen',
  'Monument Green': 'Monument groen',
  'Silver Grey': 'Zilvergrijs',
  'Signal Grey': 'Signaalgrijs',
  'Basalt Grey': 'Basaltgrijs',
  'Slate Grey': 'Leigrijs',
  Anthracite: 'Antraciet',
  'Smooth Anthracite': 'Antraciet',
  'Black Grey': 'Zwartgrijs',
  'Stone Grey': 'Steengrijs',
  'Light Grey': 'Lichtgrijs',
  'Quartz Grey': 'Kwartsgrijs',
  'Silk Grey': 'Zijdegrijs',
  'Golden Oak': 'Goud eiken',
  'Black Brown': 'Zwartbruin',
  Cream: 'Crème',
  'Black Woodgrain Ligna': 'Zwart Ligna',
  White: 'Reinwit',
  'White Smooth': 'Wit glad',
};

const toVmkDutchColorName = (colorData, fallbackRal) => {
  if (!colorData?.name) return fallbackRal ? `RAL ${fallbackRal}` : '';
  return VMK_DUTCH_COLOR_NAMES[colorData.name] || colorData.name;
};

const formatVmkExteriorColor = (colorId) => {
  const color = colorId ? getColorById(colorId) : null;
  const ral = color?.ral || '9010';
  const name = toVmkDutchColorName(color, ral);

  if (ral === '7016') return `Antraciet houtnerf buiten ${ral}`;
  if (ral === '9010') return `Reinwit houtnerf ${ral} buiten`;
  return `${name} houtnerf ${ral} buiten`;
};

const formatVmkInteriorColor = (colorId) => {
  const color = colorId ? getColorById(colorId) : null;
  let ral = color?.ral || '9016';
  if (colorId === 'white' || colorId === 'ral-9010') {
    ral = '9016';
  }
  return `Binnen glad ${ral}`;
};

export const formatVmkColorLine = (exteriorColorId, interiorColorId) =>
  `(${formatVmkExteriorColor(exteriorColorId)} - ${formatVmkInteriorColor(interiorColorId)})`;

export const getGlassDimensionsForPart = (partWidthMm, heightMm, parts = 1) => {
  const glassW =
    parts === 1
      ? Math.max(0, partWidthMm - FIXED_FRAME_OFFSET_MM)
      : Math.max(0, partWidthMm - MULTI_PART_GLASS_WIDTH_OFFSET_MM);
  const glassH = Math.max(0, heightMm - FIXED_FRAME_OFFSET_MM);
  return { width: glassW, height: glassH };
};

export const getGlassDimensionRows = (config) => {
  const width = config.width || 0;
  const height = config.height || 0;
  const parts = config.parts || 1;
  const partWidths = getPartWidths(config, parts, width);

  return partWidths.map((partWidth) => getGlassDimensionsForPart(partWidth, height, parts));
};

const getColorIds = (config) => {
  const exterior = config.exteriorColor || config.color || 'anthracite';
  const interior = config.interiorFrameColor || config.interiorColor || 'ral-9016';
  return { exterior, interior };
};

/** @returns {{ description: string, subline?: string, dim1?: string, dim2?: string }[]} */
export const buildWindowSpecRows = (config) => {
  const pageType = detectPageType(config);
  const { exterior, interior } = getColorIds(config);
  const colorLine = formatVmkColorLine(exterior, interior);
  const rows = [];

  rows.push({
    description: '9495 Blokkader 15 graden met aanslag met afwerknaad',
    subline: colorLine,
    dim1: '90mm',
    dim2: '120mm',
  });

  getGlassDimensionRows(config).forEach((dims) => {
    rows.push({
      description: getVmkGlazingLabel(config),
      dim1: formatMm(dims.width),
      dim2: formatMm(dims.height),
    });
  });

  return rows;
};

/** @returns {{ description: string, subline?: string, dim1?: string, dim2?: string }[]} */
export const buildDoorSpecRows = (config) => {
  const pageType = detectPageType(config);
  const { exterior, interior } = getColorIds(config);
  const colorLine = formatVmkColorLine(exterior, interior);
  const width = config.width || 0;
  const height = config.height || 0;
  const glassDims = getGlassDimensionsForPart(width, height, 1);

  const rows = [
    {
      description: '9491 Blokkader 15 graden met aanslag zonder afwerknaad',
      subline: colorLine,
      dim1: '90mm',
      dim2: '120mm',
    },
    {
      description: 'BUVA dorpel PS0555 binnendraaiend',
      subline: '()',
      dim1: '55mm',
      dim2: '132mm',
    },
    {
      description: '9491 Blokkader 15 graden met aanslag zonder afwerknaad',
      subline: colorLine,
      dim1: '90mm',
      dim2: '120mm',
    },
    {
      description: '9433 Voordeur vleugel',
      subline: colorLine,
      dim1: '125mm',
      dim2: '82mm',
    },
    {
      description: '9461 T-profiel vlak 82-92',
      subline: colorLine,
      dim1: '92mm',
      dim2: '82mm',
    },
    {
      description: getVmkGlazingLabel(config),
      dim1: formatMm(glassDims.width),
      dim2: formatMm(Math.min(glassDims.height, height - 313)),
    },
  ];

  if (pageType === 'voordeur' && config.doorStyle !== 'style-4') {
    const panelWidth = Math.max(0, Math.round(width * 0.45));
    rows.push({
      description: 'Sandwich 24mm',
      subline: colorLine,
      dim1: formatMm(panelWidth),
      dim2: formatMm(Math.max(0, height - 313)),
    });
  }

  rows.push(
    { description: 'RVS opdekscharnieren 4x' },
    { description: 'Dom vh dubb.cilinder Plura 333 45-60** kl.1' },
    { description: 'KT Rozet SKG-3' },
    { description: 'Deurgreep stanggreep 800 buiten schuine pootjes' },
    { description: "Extra's" }
  );

  if (config.woodJoint === 'joint-90' || config.hvlSurcharge === true) {
    rows.push({
      description: 'Toeslag HVL',
      unitPrice: calcVoordeurWoodJointPremium(config),
    });
  }

  return rows;
};

/** @returns {{ description: string, subline?: string, dim1?: string, dim2?: string }[]} */
export const buildKeralitSpecRows = (config) => {
  const { exterior, interior } = getColorIds(config);
  const colorLine = formatVmkColorLine(exterior, interior);
  const width = config.width || 0;
  const height = config.height || 0;
  const areaSqm = (width * height) / 1_000_000;

  return [
    {
      description: getKeralitProfileName(config.profileSystem),
      subline: colorLine,
      dim1: formatSqm(areaSqm),
      dim2: '',
    },
  ];
};

export const buildSpecRows = (config, quoteType) => {
  if (isKeralitQuote(config, quoteType)) {
    return buildKeralitSpecRows(config?.config || config);
  }
  const pageType = detectPageType(config);
  if (pageType === 'voordeur' || pageType === 'achterdeur' || pageType === 'terrasdeur') {
    return buildDoorSpecRows(config);
  }
  return buildWindowSpecRows(config);
};

/** Total glazing pane area (m²) — matches HR++ rows on the VMK quote table. */
export const getTotalGlazingAreaSqm = (config) =>
  getConfiguratorGlazingAreaSqm(config, detectPageType(config));

export const getItemHeaderSubtitle = (config) => {
  const pageType = detectPageType(config);
  if (pageType === 'voordeur' || pageType === 'achterdeur' || pageType === 'terrasdeur') {
    return VMK_DOOR_SUBTITLE;
  }
  return null;
};

export const formatVmkUnitPrice = (unitPrice, quantity, lineTotal, formatPrice) =>
  `${quantity}x  ${formatPrice(unitPrice).replace('\u00a0', ' ')} =  ${formatPrice(lineTotal).replace('\u00a0', ' ')}`;

export const formatVmkDate = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

/** Long Dutch date for cover page, e.g. "10 juni 2026" */
export const formatVmkCoverDate = (date = new Date()) => {
  const d = date instanceof Date ? date : new Date(date);
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
};
